import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Form } from "antd";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import useNotification from "../../hooks/newUXNotification";

import { useLoading } from "../../hooks/loading";

import { useDispatch, useSelector } from "react-redux";

import {
  getEvents,
  getAccountsByEventAndTypeUser,
  getOpportunitiesByEventAndAccount,
  changeAdminStaffSignIn,
  handleReturnToUser,
} from "../../redux/modules/impersonate/actions";
import { authSuccess } from "../../redux/modules/auth/actions";
import { cleanQuataCredential } from "../../redux/modules/credentials/actions";
import { changeApplicationLanguage } from "../../redux/modules/language/actions";
import {
  clearInitialSettingsState,
  addInitialSettingsSuccess,
} from "../../redux/modules/initialSettings/actions";
import { clearChecklist } from "../../redux/modules/checklist/actions";
import { clearAccounts } from "../../redux/modules/account/actions";
import { clearProfileExhibitor } from "../../redux/modules/profile/actions";
import { clearProfileServiceProvider } from "../../redux/modules/profile/actions";

import Button from "../../newUXComponents/Button";
import Select from "../../newUXComponents/Select";

import { Container, Title, Hr, Content, ContainerButton } from "./styles";

import { INITIAL_VALUES, validation } from "./validations";

const ImpersonateUser = () => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const history = useHistory();
  const dispatch = useDispatch();
  const notification = useNotification();
  const { handleAccountLoading } = useLoading();

  const { adminToken, user, keepSignedIn } = useSelector((state) => state.auth);
  const { events } = useSelector((state) => state.impersonate);

  const [accountsOptions, setAccountsOptions] = useState([]);
  const [opportunitiesOptions, setOpportunitiesOptions] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);

  const typeUser = useMemo(
    () => [
      {
        label: intl.formatMessage({ id: "exhibitor" }),
        value: "EXHIBITOR",
      },
      {
        label: intl.formatMessage({ id: "sponsor_without_m2" }),
        value: "SPONSOR_WITHOUT_M2",
      },
      {
        label: intl.formatMessage({ id: "digital_only" }),
        value: "DIGITAL_ONLY",
      },
      {
        label: intl.formatMessage({ id: "service_provider" }),
        value: "SERVICE_PROVIDER",
      },
    ],
    [intl]
  );

  const callbackError = useCallback(
    (message) => {
      handleAccountLoading(false);
      if (message === -2025) {
        notification.displayInfo(
          intl.formatMessage({
            id: "api.error." + message,
          }),
          "info-impersonate"
        );
      } else {
        notification.displayError(
          intl.formatMessage({
            id: "api.error." + message,
          }),
          "error-impersonate"
        );
      }
    },
    [notification, intl, handleAccountLoading]
  );

  const callbackSetAccounts = useCallback(
    (data) => {
      handleAccountLoading(false);
      setAccountsOptions(data);
    },
    [handleAccountLoading]
  );

  const callbackSetOpportunities = useCallback(
    (data) => {
      handleAccountLoading(false);
      setOpportunitiesOptions(data);
    },
    [handleAccountLoading]
  );

  const callbackSuccess = useCallback(
    (accountData) => {
      handleAccountLoading(false);
      dispatch(
        authSuccess({
          user: accountData.user,
          token: accountData.token,
          customerCenterToken: accountData.customerCenterToken,
          accountCreated: accountData.accountCreated,
          keepSignedIn,
          serviceProviderLoggingAsAnExhibitor:
            accountData.serviceProviderLoggingAsAnExhibitor,
          adminToken: accountData.adminToken,
        })
      );
      dispatch(
        changeApplicationLanguage({
          code: accountData.user.language,
        })
      );
      if (accountData.initialSettings) {
        dispatch(addInitialSettingsSuccess(accountData.initialSettings));
      } else {
        dispatch(clearInitialSettingsState());
      }
      dispatch(clearChecklist());
      dispatch(clearAccounts());
      dispatch(clearProfileExhibitor());
      dispatch(clearProfileServiceProvider());
      dispatch(cleanQuataCredential());

      if (accountData.adminToken) {
        history.push("/home");
      } else {
        history.push("/dashboard");
      }
    },
    [dispatch, handleAccountLoading, history, keepSignedIn]
  );

  const onFinish = (data) => {
    handleAccountLoading(true);

    dispatch(
      changeAdminStaffSignIn(
        {
          params: {
            systemUserId: data.account,
            loggedInUserId: user?.id,
            eventId: data.event,
            opportunityId: data.opportunity,
          },
          adminToken,
        },
        callbackSuccess,
        callbackError
      )
    );
  };

  const onChangeEvent = (event) => {
    form.setFieldValue("type", "");
    form.setFieldValue("account", "");
    form.setFieldValue("opportunity", "");
    setSelectedEvent(event);
  };

  const handleGetAccounts = (type) => {
    handleAccountLoading(true);
    form.setFieldValue("account", "");
    form.setFieldValue("opportunity", "");

    if (selectedEvent) {
      dispatch(
        getAccountsByEventAndTypeUser(
          {
            params: {
              eventId: selectedEvent,
              roleType: type,
              useEmailInTheServiceProviderLabel: false,
              useCancelledOpportunities: true,
            },
            adminToken,
          },
          callbackSetAccounts,
          callbackError
        )
      );
    }
  };

  const handleGetOpportunities = (account) => {
    handleAccountLoading(true);
    form.setFieldValue("opportunity", "");
    setSelectedAccount(account);

    if (selectedEvent && account) {
      dispatch(
        getOpportunitiesByEventAndAccount(
          {
            params: {
              eventId: selectedEvent,
              userId: account,
            },
            adminToken,
          },
          callbackSetOpportunities,
          callbackError
        )
      );
    }
  };

  const returnToUser = () => {
    dispatch(
      handleReturnToUser(
        {
          params: {
            systemUserId: 0,
            loggedInUserId: user?.id,
            eventId: 0,
            opportunityId: 0,
          },
          adminToken,
        },
        callbackSuccess,
        callbackError
      )
    );
  };

  useEffect(() => {
    if (adminToken) {
      dispatch(
        getEvents(
          {
            params: {
              eventEditionCode: "",
              eventEditionName: "",
              active: true,
            },
            adminToken,
          },
          callbackError
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken]);

  return (
    <Container>
      <Title>{intl.formatMessage({ id: "accounts_users" })}</Title>
      <Hr />
      <Content>
        <Form
          layout="vertical"
          form={form}
          name="impersonate-user-form"
          onFinish={onFinish}
          initialValues={INITIAL_VALUES}
        >
          <Select
            name="event"
            label={intl.formatMessage({ id: "event_edition" })}
            allowClear
            placeholder={intl.formatMessage({ id: "event_edition" })}
            rules={validation("event", intl)}
            options={events}
            onChange={(value) => onChangeEvent(value)}
          />
          <Select
            name="type"
            label={intl.formatMessage({ id: "type" })}
            allowClear
            placeholder={intl.formatMessage({ id: "type" })}
            rules={validation("type", intl)}
            options={typeUser}
            onChange={(value) => handleGetAccounts(value)}
          />
          <Select
            name="account"
            label={intl.formatMessage({ id: "account" })}
            allowClear
            placeholder={intl.formatMessage({ id: "account" })}
            rules={validation("account", intl)}
            options={accountsOptions}
            onChange={(value) => handleGetOpportunities(value)}
            disabled={!accountsOptions.length}
          />
          <Select
            name="opportunity"
            label={intl.formatMessage({ id: "contract" })}
            allowClear
            placeholder={intl.formatMessage({ id: "contract" })}
            rules={validation("opportunity", intl)}
            options={opportunitiesOptions}
            disabled={!opportunitiesOptions.length}
            onChange={(value) => setSelectedOpportunity(value)}
          />

          <ContainerButton>
            <Button
              type="link"
              label={intl.formatMessage({ id: "return_to_user" })}
              onClick={() => returnToUser()}
            />
            <Button
              type="primary"
              label={intl.formatMessage({ id: "sidebar.impersonate_user" })}
              htmlType="submit"
            />
          </ContainerButton>
        </Form>
      </Content>
    </Container>
  );
};

export default ImpersonateUser;
